import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Question } from '../../types';
import {
  personCareQuestions,
  caregiverQuestions,
  termsQuestions,
} from '../../config/quizQuestions';

// Deep clone helper to avoid mutation issues
const cloneQuestions = (questions: Question[]): Question[] => JSON.parse(JSON.stringify(questions));

interface QuizState {
  personCare: {
    questions: Question[];
    currentIndex: number;
  };
  caregiverCare: {
    questions: Question[];
    currentIndex: number;
  };
  termsAndConditions: {
    questions: Question[];
    currentIndex: number;
  };
  editSummary: string;
  editingSection: string | null;
  editingIndex: number | null;
}

const initialState: QuizState = {
  personCare: {
    questions: cloneQuestions(personCareQuestions),
    currentIndex: 0,
  },
  caregiverCare: {
    questions: cloneQuestions(caregiverQuestions),
    currentIndex: 0,
  },
  termsAndConditions: {
    questions: cloneQuestions(termsQuestions),
    currentIndex: 0,
  },
  editSummary: '',
  editingSection: null,
  editingIndex: null,
};

const quizSlice = createSlice({
  name: 'quiz',
  initialState,
  reducers: {
    // Person Care Actions
    setPersonCareAnswer: (
      state,
      action: PayloadAction<{ index: number; answer: string; fileName?: string }>
    ) => {
      const { index, answer, fileName } = action.payload;
      if (state.personCare.questions[index]) {
        state.personCare.questions[index].answers = answer;
        if (fileName !== undefined) {
          state.personCare.questions[index].fileName = fileName;
        }
      }
    },
    setPersonCareIndex: (state, action: PayloadAction<number>) => {
      state.personCare.currentIndex = action.payload;
    },
    updatePersonCareQuestions: (state, action: PayloadAction<Question[]>) => {
      state.personCare.questions = action.payload;
    },

    // Caregiver Care Actions
    setCaregiverAnswer: (
      state,
      action: PayloadAction<{ index: number; answer: string; fileName?: string }>
    ) => {
      const { index, answer, fileName } = action.payload;
      if (state.caregiverCare.questions[index]) {
        state.caregiverCare.questions[index].answers = answer;
        if (fileName !== undefined) {
          state.caregiverCare.questions[index].fileName = fileName;
        }
      }
    },
    setCaregiverIndex: (state, action: PayloadAction<number>) => {
      state.caregiverCare.currentIndex = action.payload;
    },
    updateCaregiverQuestions: (state, action: PayloadAction<Question[]>) => {
      state.caregiverCare.questions = action.payload;
    },

    // Terms Actions
    setTermsAnswer: (
      state,
      action: PayloadAction<{ index: number; answer: string; fileName?: string }>
    ) => {
      const { index, answer, fileName } = action.payload;
      if (state.termsAndConditions.questions[index]) {
        state.termsAndConditions.questions[index].answers = answer;
        if (fileName !== undefined) {
          state.termsAndConditions.questions[index].fileName = fileName;
        }
      }
    },
    setTermsIndex: (state, action: PayloadAction<number>) => {
      state.termsAndConditions.currentIndex = action.payload;
    },
    updateTermsQuestions: (state, action: PayloadAction<Question[]>) => {
      state.termsAndConditions.questions = action.payload;
    },

    // Edit Mode Actions
    setEditMode: (
      state,
      action: PayloadAction<{ section: string; index: number; previousValue: string }>
    ) => {
      state.editingSection = action.payload.section;
      state.editingIndex = action.payload.index;
      state.editSummary = action.payload.previousValue;
    },
    clearEditMode: (state) => {
      state.editingSection = null;
      state.editingIndex = null;
      state.editSummary = '';
    },

    // Bulk Update (for loading from API)
    loadQuizData: (
      state,
      action: PayloadAction<{
        personCare?: { questions: Question[]; currentIndex: number };
        caregiverCare?: { questions: Question[]; currentIndex: number };
        termsAndConditions?: { questions: Question[]; currentIndex: number };
      }>
    ) => {
      if (action.payload.personCare) {
        state.personCare = action.payload.personCare;
      }
      if (action.payload.caregiverCare) {
        state.caregiverCare = action.payload.caregiverCare;
      }
      if (action.payload.termsAndConditions) {
        state.termsAndConditions = action.payload.termsAndConditions;
      }
    },

    // Reset Quiz
    resetQuiz: () => initialState,

    // Reset Caregiver Questions (used when switching from Nein to Ja)
    resetCaregiverQuestions: (state) => {
      state.caregiverCare = {
        questions: cloneQuestions(caregiverQuestions),
        currentIndex: 0,
      };
    },

    // Reset Terms Questions (used when user needs to re-sign due to new data)
    resetTermsQuestions: (state) => {
      state.termsAndConditions = {
        questions: cloneQuestions(termsQuestions),
        currentIndex: 0,
      };
    },
  },
});

export const {
  setPersonCareAnswer,
  setPersonCareIndex,
  updatePersonCareQuestions,
  setCaregiverAnswer,
  setCaregiverIndex,
  updateCaregiverQuestions,
  setTermsAnswer,
  setTermsIndex,
  updateTermsQuestions,
  setEditMode,
  clearEditMode,
  loadQuizData,
  resetQuiz,
  resetCaregiverQuestions,
  resetTermsQuestions,
} = quizSlice.actions;

export default quizSlice.reducer;
